import { FC, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useServerEnv } from 'frontStaticAppPath';

import { Layer, Title, TitleProps, useBreakpoint } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import styles from './styles.less';

interface Props {
    title: TitleProps['children'];
    description?: TitleProps['description'];
    visible: boolean;
    onClose: () => void;
    options?: ReactNode;
    buttons?: ReactNode;
    dataQaContainer?: string;
    dataQaCloseButton?: string;
    dataQaBackdrop?: string;
    children: ReactNode;
}

const Drawer: FC<Props> = ({
    visible,
    title,
    description,
    onClose,
    options,
    buttons,
    children,
    dataQaContainer = 'drawer',
    dataQaCloseButton = 'drawer-close-button',
    dataQaBackdrop = 'drawer-backdrop',
}) => {
    const { isMobile } = useBreakpoint();
    const isSSR = useServerEnv();

    useEffect(() => {
        if (visible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [visible]);

    if (isSSR || isMobile) {
        return null;
    }

    return createPortal(
        <>
            <Layer layer="iota">
                <div className={classNames(styles.drawer, { [styles.drawerOpen]: visible })} data-qa={dataQaContainer}>
                    <div className={styles.drawerContainer}>
                        <div className={styles.header}>
                            <div className={styles.headerContent}>
                                <Title size="medium" Element="h2" description={description}>
                                    {title}
                                </Title>
                                <div className={styles.headerClose} onClick={onClose} data-qa={dataQaCloseButton}>
                                    <CrossOutlinedSize24 initial="secondary" />
                                </div>
                            </div>
                            {options && <div className={styles.headerOptions}>{options}</div>}
                        </div>
                        <div className={styles.drawerContent}>{children}</div>
                        {buttons && <div className={styles.drawerButtons}>{buttons}</div>}
                    </div>
                </div>
            </Layer>
            <Layer layer="alpha">
                <div
                    className={classNames(styles.backdrop, {
                        [styles.backdropOpen]: visible,
                    })}
                    onClick={onClose}
                    data-qa={dataQaBackdrop}
                />
            </Layer>
        </>,
        document.body
    );
};

export default Drawer;
